import { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import toast from 'react-hot-toast';
import { Dialog } from '@headlessui/react';
import {
  BuildingOffice2Icon,
  EnvelopeIcon,
  PhoneIcon,
  UserIcon,
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ArrowPathIcon,
  UserGroupIcon,
  CircleStackIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';

const statusConfig = {
  new: { label: 'New', color: 'bg-blue-500/20 text-blue-400', icon: ClockIcon },
  in_progress: { label: 'In Progress', color: 'bg-amber-500/20 text-amber-400', icon: ArrowPathIcon },
  converted: { label: 'Converted', color: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircleIcon },
  closed: { label: 'Closed', color: 'bg-surface-500/20 text-surface-400', icon: XCircleIcon }
};

const estimatedUsersLabels = {
  '10-25': '10-25 users',
  '25-50': '25-50 users',
  '50-100': '50-100 users',
  '100+': '100+ users'
};

const estimatedStorageLabels = {
  '500GB-1TB': '500 GB - 1 TB',
  '1TB-5TB': '1 - 5 TB',
  '5TB+': '5+ TB',
  'not_sure': 'Not sure'
};

function formatDate(date) {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

// ==================== INQUIRY DETAIL MODAL ====================
function InquiryDetailModal({ isOpen, onClose, inquiryId, onUpdate }) {
  const [inquiry, setInquiry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [admins, setAdmins] = useState([]);

  useEffect(() => {
    if (isOpen && inquiryId) {
      loadInquiry();
      loadAdmins();
    }
  }, [isOpen, inquiryId]);

  const loadInquiry = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getEnterpriseInquiry(inquiryId);
      setInquiry(response.data.inquiry);
    } catch (error) {
      toast.error('Failed to load inquiry');
    } finally {
      setLoading(false);
    }
  };

  const loadAdmins = async () => {
    try {
      const response = await adminApi.getAvailableAdmins();
      setAdmins(response.data.admins);
    } catch (error) {
      console.error('Failed to load admins:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      await adminApi.addEnterpriseMessage(inquiryId, newMessage);
      setNewMessage('');
      loadInquiry();
      onUpdate();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (status) => {
    try {
      await adminApi.updateEnterpriseInquiry(inquiryId, { status });
      toast.success('Status updated');
      loadInquiry();
      onUpdate();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAssign = async (adminId) => {
    try {
      await adminApi.updateEnterpriseInquiry(inquiryId, { assignedTo: adminId || null });
      toast.success('Assignment updated');
      loadInquiry();
      onUpdate();
    } catch (error) {
      toast.error('Failed to update assignment');
    }
  };

  const handleConvert = async () => {
    // TODO: Open enterprise company creation modal
    toast.info('Enterprise company creation coming soon');
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-end">
        <Dialog.Panel className="h-full w-full max-w-2xl bg-surface-900 border-l border-surface-700 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-surface-700">
            <Dialog.Title className="text-lg font-semibold text-surface-100">
              Inquiry Details
            </Dialog.Title>
            <button onClick={onClose} className="text-surface-400 hover:text-surface-100">
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : inquiry ? (
            <>
              {/* Content */}
              <div className="flex-1 overflow-y-auto">
                {/* Company Info */}
                <div className="p-6 border-b border-surface-700">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                      <BuildingOffice2Icon className="w-6 h-6 text-purple-400" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-xl font-semibold text-surface-100">{inquiry.companyName}</h2>
                      <div className="mt-2 space-y-1 text-sm">
                        <div className="flex items-center gap-2 text-surface-300">
                          <UserIcon className="w-4 h-4 text-surface-500" />
                          {inquiry.contactName}
                        </div>
                        <div className="flex items-center gap-2 text-surface-300">
                          <EnvelopeIcon className="w-4 h-4 text-surface-500" />
                          <a href={`mailto:${inquiry.email}`} className="text-primary-400 hover:underline">
                            {inquiry.email}
                          </a>
                        </div>
                        {inquiry.phone && (
                          <div className="flex items-center gap-2 text-surface-300">
                            <PhoneIcon className="w-4 h-4 text-surface-500" />
                            {inquiry.phone}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Requirements */}
                  <div className="mt-4 grid grid-cols-2 gap-4">
                    <div className="bg-surface-800 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-surface-400 text-sm mb-1">
                        <UserGroupIcon className="w-4 h-4" />
                        Estimated Users
                      </div>
                      <div className="text-surface-100 font-medium">
                        {estimatedUsersLabels[inquiry.estimatedUsers] || inquiry.estimatedUsers}
                      </div>
                    </div>
                    <div className="bg-surface-800 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-surface-400 text-sm mb-1">
                        <CircleStackIcon className="w-4 h-4" />
                        Estimated Storage
                      </div>
                      <div className="text-surface-100 font-medium">
                        {estimatedStorageLabels[inquiry.estimatedStorage] || inquiry.estimatedStorage}
                      </div>
                    </div>
                  </div>

                  {/* Initial Message */}
                  <div className="mt-4 bg-surface-800 rounded-lg p-4">
                    <div className="text-sm text-surface-400 mb-2">Initial Message</div>
                    <p className="text-surface-200 whitespace-pre-wrap">{inquiry.message}</p>
                  </div>

                  {/* Status & Assignment */}
                  <div className="mt-4 grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-surface-400 mb-1">Status</label>
                      <select
                        value={inquiry.status}
                        onChange={(e) => handleStatusChange(e.target.value)}
                        className="input"
                        disabled={inquiry.status === 'converted'}
                      >
                        <option value="new">New</option>
                        <option value="in_progress">In Progress</option>
                        <option value="closed">Closed</option>
                        <option value="converted" disabled>Converted</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-surface-400 mb-1">Assigned To</label>
                      <select
                        value={inquiry.assignedTo?._id || ''}
                        onChange={(e) => handleAssign(e.target.value)}
                        className="input"
                      >
                        <option value="">Unassigned</option>
                        {admins.map((admin) => (
                          <option key={admin._id} value={admin._id}>
                            {admin.firstName} {admin.lastName}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Convert Button */}
                  {inquiry.status !== 'converted' && (
                    <button
                      onClick={handleConvert}
                      className="btn-primary w-full mt-4"
                    >
                      <BuildingOffice2Icon className="w-5 h-5" />
                      Convert to Enterprise Company
                    </button>
                  )}

                  {inquiry.convertedCompanyId && (
                    <div className="mt-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 text-center">
                      <CheckCircleIcon className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                      <div className="text-emerald-400 font-medium">
                        Converted to: {inquiry.convertedCompanyId.name}
                      </div>
                    </div>
                  )}
                </div>

                {/* Messages */}
                <div className="p-6">
                  <h3 className="text-sm font-medium text-surface-400 mb-4">
                    <ChatBubbleLeftRightIcon className="w-4 h-4 inline mr-2" />
                    Conversation ({inquiry.messages?.length || 0})
                  </h3>

                  <div className="space-y-4">
                    {inquiry.messages?.map((message, index) => (
                      <div
                        key={index}
                        className={`flex ${message.sender === 'admin' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-lg p-3 ${
                            message.sender === 'admin'
                              ? 'bg-primary-500/20 text-primary-100'
                              : 'bg-surface-700 text-surface-200'
                          }`}
                        >
                          <p className="whitespace-pre-wrap">{message.content}</p>
                          <div className="text-xs mt-2 opacity-60">
                            {message.sender === 'admin' && message.sentBy && (
                              <span>{message.sentBy.firstName} • </span>
                            )}
                            {formatDate(message.sentAt)}
                          </div>
                        </div>
                      </div>
                    ))}

                    {(!inquiry.messages || inquiry.messages.length === 0) && (
                      <div className="text-center text-surface-500 py-8">
                        No messages yet. Start the conversation!
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Reply Form */}
              {inquiry.status !== 'converted' && inquiry.status !== 'closed' && (
                <form onSubmit={handleSendMessage} className="p-4 border-t border-surface-700">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your reply..."
                      className="input flex-1"
                    />
                    <button
                      type="submit"
                      disabled={sending || !newMessage.trim()}
                      className="btn-primary"
                    >
                      {sending ? (
                        <ArrowPathIcon className="w-5 h-5 animate-spin" />
                      ) : (
                        <PaperAirplaneIcon className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </form>
              )}
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-surface-400">
              Inquiry not found
            </div>
          )}
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

// ==================== MAIN COMPONENT ====================
export default function EnterpriseInquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({});
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    search: ''
  });
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  useEffect(() => {
    loadInquiries();
    loadCounts();
  }, [pagination.page, filters]);

  const loadInquiries = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getEnterpriseInquiries({
        page: pagination.page,
        limit: 20,
        ...filters
      });
      setInquiries(response.data.data || response.data.inquiries || []);
      if (response.data.pagination) {
        setPagination(response.data.pagination);
      }
    } catch (error) {
      toast.error('Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  };

  const loadCounts = async () => {
    try {
      const response = await adminApi.getEnterpriseInquiryCounts();
      setCounts(response.data);
    } catch (error) {
      console.error('Failed to load counts:', error);
    }
  };

  const handleUpdate = () => {
    loadInquiries();
    loadCounts();
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Enterprise Inquiries</h1>
          <p className="text-surface-400 mt-1">Manage enterprise contact form submissions</p>
        </div>
        {counts.newCount > 0 && (
          <div className="bg-blue-500/20 text-blue-400 px-4 py-2 rounded-lg flex items-center gap-2">
            <span className="font-bold">{counts.newCount}</span>
            <span>new inquiries</span>
          </div>
        )}
      </div>

      {/* Status Filters */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setFilters({ ...filters, status: '' })}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            filters.status === ''
              ? 'bg-primary-500 text-white'
              : 'bg-surface-800 text-surface-400 hover:text-surface-100'
          }`}
        >
          All ({counts.total || 0})
        </button>
        {Object.entries(statusConfig).map(([status, config]) => (
          <button
            key={status}
            onClick={() => setFilters({ ...filters, status })}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              filters.status === status
                ? 'bg-primary-500 text-white'
                : 'bg-surface-800 text-surface-400 hover:text-surface-100'
            }`}
          >
            <config.icon className="w-4 h-4" />
            {config.label} ({counts.counts?.[status] || 0})
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="card">
        <div className="flex items-center gap-4">
          <FunnelIcon className="w-5 h-5 text-surface-500" />
          <input
            type="text"
            placeholder="Search by company, contact, or email..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="input flex-1"
          />
        </div>
      </div>

      {/* Inquiries List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : inquiries.length === 0 ? (
        <div className="card text-center py-12">
          <BuildingOffice2Icon className="w-12 h-12 text-surface-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-surface-300 mb-2">No inquiries found</h3>
          <p className="text-surface-400">Enterprise inquiries will appear here</p>
        </div>
      ) : (
        <div className="space-y-4">
          {inquiries.map((inquiry) => {
            const StatusIcon = statusConfig[inquiry.status]?.icon || ClockIcon;
            return (
              <div
                key={inquiry._id}
                onClick={() => setSelectedInquiry(inquiry._id)}
                className="card hover:border-surface-600 cursor-pointer transition-all"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                      <BuildingOffice2Icon className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-surface-100">{inquiry.companyName}</h3>
                        {inquiry.hasUnreadMessages && (
                          <span className="w-2 h-2 bg-blue-500 rounded-full" />
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-sm text-surface-400">
                        <span className="flex items-center gap-1">
                          <UserIcon className="w-3 h-3" />
                          {inquiry.contactName}
                        </span>
                        <span className="flex items-center gap-1">
                          <EnvelopeIcon className="w-3 h-3" />
                          {inquiry.email}
                        </span>
                      </div>
                      <p className="text-sm text-surface-500 mt-2 line-clamp-2">
                        {inquiry.message}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${statusConfig[inquiry.status]?.color}`}>
                      <StatusIcon className="w-3 h-3" />
                      {statusConfig[inquiry.status]?.label}
                    </span>
                    <span className="text-xs text-surface-500">
                      {formatDate(inquiry.createdAt)}
                    </span>
                    {inquiry.assignedTo && (
                      <span className="text-xs text-surface-400">
                        Assigned to: {inquiry.assignedTo.firstName}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-surface-400 text-sm">
            Showing {inquiries.length} of {pagination.total} inquiries
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
              disabled={pagination.page === 1}
              className="btn-secondary btn-sm"
            >
              Previous
            </button>
            <button
              onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
              disabled={pagination.page === pagination.pages}
              className="btn-secondary btn-sm"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      <InquiryDetailModal
        isOpen={!!selectedInquiry}
        onClose={() => setSelectedInquiry(null)}
        inquiryId={selectedInquiry}
        onUpdate={handleUpdate}
      />
    </div>
  );
}
